import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import {
  AppExclusiveClonePlanParams,
  useCloneAppExclusiveSchedules,
  useGetAppExclusiveClonePlan,
} from '~/hooks/apis/offers/useAppExclusiveClone';

import { defaultAddDealDate, formatDayLabel, todayDate } from './appOnlyFlashCalendar';

const DATE_FORMAT = 'YYYY-MM-DD';

interface Region {
  code: string;
  name: string;
}

interface Props {
  open: boolean;
  sourceRegion: string;
  regions: Array<Region>;
  /** Calendar month (YYYY-MM) the page shows; the default range. */
  month: string;
  onClose: () => void;
}

/** The shown month from tomorrow on: days that have started can't be copied. */
function defaultRange(month: string): { from: string; to: string } {
  const first = dayjs.utc(`${month}-01`, DATE_FORMAT);
  const tomorrow = defaultAddDealDate();
  const from = first.format(DATE_FORMAT) > tomorrow ? first.format(DATE_FORMAT) : tomorrow;
  const monthEnd = first.endOf('month').format(DATE_FORMAT);
  return { from, to: monthEnd < from ? from : monthEnd };
}

function getFormError({ from, to, targetRegions }: AppExclusiveClonePlanParams): string | undefined {
  if (!from || !to) {
    return 'Choose a start and end day.';
  }
  if (to < from) {
    return 'End day must be on or after the start day.';
  }
  if (!targetRegions.length) {
    return 'Select at least one region to copy to.';
  }
  return undefined;
}

export default function CloneAppOnlyFlashScheduleDialog({ open, sourceRegion, regions, month, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState<'form' | 'review'>('form');
  const [params, setParams] = useState<AppExclusiveClonePlanParams>(() => ({
    sourceRegion,
    targetRegions: [],
    ...defaultRange(month),
  }));

  // Each open starts on the shown month and region.
  useEffect(() => {
    if (open) {
      setStep('form');
      setParams({ sourceRegion, targetRegions: [], ...defaultRange(month) });
    }
  }, [open, sourceRegion, month]);

  const sourceName = regions.find((region) => region.code === sourceRegion)?.name ?? sourceRegion;
  const targetOptions = useMemo(
    () => regions.filter((region) => region.code !== sourceRegion),
    [regions, sourceRegion],
  );
  const selectedTargets = useMemo(
    () => targetOptions.filter((region) => params.targetRegions.includes(region.code)),
    [targetOptions, params.targetRegions],
  );
  const formError = getFormError(params);

  const plan = useGetAppExclusiveClonePlan(params, open && step === 'review');
  const rows = plan.data ?? [];
  const ready = rows.filter((row) => row.slot);
  const skipped = rows.length - ready.length;
  const cloneSchedules = useCloneAppExclusiveSchedules();

  const setTargetRegions = (_event: React.SyntheticEvent, selected: Array<Region>) =>
    setParams((value) => ({ ...value, targetRegions: selected.map((region) => region.code) }));

  const setDay = (key: 'from' | 'to') => (value: dayjs.Dayjs | null) => {
    if (value?.isValid()) {
      setParams((current) => ({ ...current, [key]: value.format(DATE_FORMAT) }));
    }
  };

  const handleClone = async () => {
    try {
      const result = await cloneSchedules.mutateAsync(ready);
      const failedNote = result.failed.length
        ? `; ${result.failed.length} failed (${result.failed
            .map(({ row, message }) => `${row.offerName} ${row.region}: ${message}`)
            .join('; ')})`
        : '';
      enqueueSnackbar(`Copied ${result.created} deal${result.created === 1 ? '' : 's'}${failedNote}`, {
        variant: result.failed.length ? 'warning' : 'success',
      });
      onClose();
    } catch (err) {
      enqueueSnackbar(`Could not copy schedule: ${err?.message ?? 'Unknown error'}`, { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="clone-app-only-flash-title">
      <DialogTitle id="clone-app-only-flash-title">
        {step === 'form' ? 'Copy schedule' : 'Review copied schedule'}
      </DialogTitle>
      <DialogContent>
        {step === 'form' ? (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Copy upcoming {sourceName} ({sourceRegion}) deals to other regions on the same days. Existing deals in
              those regions are kept.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <DatePicker
                label="From"
                format="ddd D MMM YYYY"
                value={dayjs(params.from, DATE_FORMAT)}
                minDate={dayjs(todayDate(), DATE_FORMAT)}
                onChange={setDay('from')}
                slotProps={{ textField: { required: true } }}
              />
              <DatePicker
                label="To"
                format="ddd D MMM YYYY"
                value={dayjs(params.to, DATE_FORMAT)}
                minDate={dayjs(params.from, DATE_FORMAT)}
                onChange={setDay('to')}
                slotProps={{ textField: { required: true } }}
              />
            </Stack>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={targetOptions}
              value={selectedTargets}
              onChange={setTargetRegions}
              getOptionLabel={(region) => `${region.name} (${region.code})`}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              renderOption={({ key, ...optionProps }, region, { selected }) => (
                <li key={key} {...optionProps}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                  {region.name} ({region.code})
                </li>
              )}
              renderInput={(inputParams) => (
                <TextField
                  {...inputParams}
                  label="Copy to"
                  placeholder={selectedTargets.length ? undefined : 'Search regions'}
                  required
                  error={!!formError && !params.targetRegions.length}
                  helperText={formError}
                />
              )}
            />
            <Alert severity="info">
              Each deal is checked in the target region first. Deals not on sale there for their day plus the 3 days
              after, or landing on a taken day, are flagged and skipped.
            </Alert>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {plan.isFetching && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Checking each deal in {params.targetRegions.join(', ')}...
                </Typography>
              </Stack>
            )}
            {plan.isError && <Alert severity="error">Could not build the copy: {plan.error?.message}</Alert>}
            {plan.isSuccess && (
              <>
                <Typography variant="body2">
                  {formatDayLabel(params.from)} – {formatDayLabel(params.to)} from {sourceName}:{' '}
                  <strong>{ready.length} ready</strong>, {skipped} skipped.
                </Typography>
                {rows.length === 0 ? (
                  <Alert severity="info">No upcoming deals start in this range in {sourceName}.</Alert>
                ) : (
                  <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Offer</TableCell>
                          <TableCell>Day</TableCell>
                          <TableCell>Region</TableCell>
                          <TableCell>Result</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row) => (
                          <TableRow key={row.key}>
                            <TableCell>{row.offerName}</TableCell>
                            <TableCell>{formatDayLabel(row.date)}</TableCell>
                            <TableCell>{row.region}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                {row.slot ? (
                                  <CheckCircleOutlineIcon fontSize="small" color="success" />
                                ) : (
                                  <WarningAmberIcon fontSize="small" color="warning" />
                                )}
                                <Typography variant="body2">{row.reason ?? 'Ready to copy'}</Typography>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'review' && (
          <Button variant="text" onClick={() => setStep('form')} sx={{ mr: 'auto' }}>
            Back
          </Button>
        )}
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        {step === 'form' ? (
          <Button variant="contained" onClick={() => setStep('review')} disabled={!!formError}>
            Review
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleClone}
            disabled={plan.isFetching || !ready.length || cloneSchedules.isPending}
          >
            {cloneSchedules.isPending ? 'Copying...' : `Copy ${ready.length} deal${ready.length === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
